import { useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import { Box, Grid, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PropTypes from 'prop-types';

// Our components
import Alert from 'components/Alert';
import ExportableReport from 'components/ExportableReport/ExportableReport';
import SoraWalletOnboardingSource from 'components/SoraWallet/SoraWalletHeading/SoraWalletOnboardingSource';
import RefreshClientDataModal from 'components/FeatureFlag/RefreshClientDataModal';
import SendInsightsReportModal from 'components/FeatureFlag/SendInsightsReportModal';
import Loader from 'components/Loader/index';
import { TertiaryButton, TextButton } from 'components/Button/Button';

// Our hooks
import { useIsFeatureFlagCompany } from 'shared/utils/featureflags/isFeatureFlagCompany';
import useMutateContactClient from 'hooks/useMutateContactClient';
import useMutateRefreshSingleClient from 'hooks/dataRefresh/useMutateRefreshSingleClient';
import useGetBurnDownChart from 'hooks/Burdown/queries/useGetBurnDownChart';
import useGetWalletOverview from 'hooks/soraWallet/useGetWalletOverview';
import useMutateSendGenericEmail from 'hooks/emails/useMutateSendGenericEmail';

// Our Utils
import getTimeAgo from 'shared/utils/date/getTimeAgo';
import getLastUpdatedTimeStamp from 'shared/utils/date/getLastUpdatedTimeStamp';

function SoraWalletHeading({
	client,
	isClient,
	onboardingSourceType,
	referralPartner,
	advisorName,
	advisor
}) {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isSendReportModalOpen, setIsSendReportModalOpen] = useState(false);

	const clientName = `${client.firstName} ${client.lastName}`;
	const contactSubmissionMutation = useMutateContactClient();
	const { isLoading, isSuccess } = useGetBurnDownChart();

	const refreshData = useMutateRefreshSingleClient();

	const isFeatureFlagCompany = useIsFeatureFlagCompany(advisor?.companyId);

	const { data: lastUpdatedTimeStamp } = useGetWalletOverview(
		getLastUpdatedTimeStamp
	);

	const exportableReportRef = useRef(null);

	const handlePrint = useReactToPrint({
		documentTitle: `${clientName}'s_Report`,
		contentRef: exportableReportRef
	});

	const { isError: contactClientError, isSuccess: contactClientSuccess } =
		contactSubmissionMutation;

	const { isSuccess: isRefreshDataSuccess, isError: isRefreshDataError } =
		refreshData;

	const isAdvisor = !isClient;

	const timeAgo = refreshData?.data
		? getTimeAgo(refreshData?.data)
		: getTimeAgo(lastUpdatedTimeStamp);

	return (
		<Grid
			container
			sx={{ justifyContent: 'space-between', alignItems: 'flex-end' }}
		>
			{isAdvisor && (contactClientSuccess || isRefreshDataSuccess) && (
				<Alert variant="success" />
			)}
			{isAdvisor && (contactClientError || isRefreshDataError) && (
				<Alert variant="error" />
			)}

			<Grid
				container
				item
				xs={12}
				sm={10}
				sx={{ alignItems: 'flex-start' }}
			>
				<Box
					sx={{
						marginBottom: {
							xs: 2,
							sm: 0
						}
					}}
				>
					<Typography
						variant="h1Gascogne"
						sx={{ margin: '8px 32px 0px 8px' }}
					>
						{clientName}&rsquo;s Wallet
					</Typography>
					<SoraWalletOnboardingSource
						onboardingSourceType={onboardingSourceType}
						referralPartner={referralPartner}
						advisorName={advisorName}
						advisor={advisor}
						client={client}
					/>
				</Box>

				{isAdvisor && isSuccess && (
					<Box sx={{ display: 'flex', gap: 2 }}>
						<TertiaryButton
							ariaDescribedby=""
							isDisabled={false}
							onClick={(e) => {
								e.stopPropagation();
								handlePrint();
							}}
							sx={{
								marginBottom: {
									xs: 2,
									sm: 0
								}
							}}
						>
							Insights Report
						</TertiaryButton>
						<TertiaryButton
							ariaDescribedby=""
							isDisabled={false}
							onClick={(e) => {
								e.stopPropagation();
								setIsSendReportModalOpen(true);
							}}
							sx={{
								marginBottom: {
									xs: 2,
									sm: 0
								}
							}}
						>
							Send Insights Report
						</TertiaryButton>
					</Box>
				)}
			</Grid>

			<Grid item>
				<TextButton
					aria-label="refresh"
					disabled={refreshData.isLoading}
					onClick={(e) => {
						e.stopPropagation();
						if (isFeatureFlagCompany) {
							setIsModalOpen(true);
						} else {
							refreshData.mutate({});
						}
					}}
					sx={{
						color: '#323232',
						textDecoration: 'none'
					}}
				>
					{refreshData.isLoading && (
						<RefreshIcon
							sx={{
								color: '#323232',
								marginRight: 1,
								'@keyframes rotateAnimation': {
									from: { transform: 'rotate(0deg)' },
									to: { transform: 'rotate(360deg)' }
								},
								animation: 'rotateAnimation 2s linear infinite'
							}}
						/>
					)}

					{!refreshData.isLoading && (
						<RefreshIcon
							sx={{
								color: '#323232',
								marginRight: 1
							}}
						/>
					)}

					<Typography
						variant="caption"
						sx={{ lineHeight: '1.5' }}
					>{`Sora Synced ${timeAgo} ago`}</Typography>
				</TextButton>
				<RefreshClientDataModal
					isModalOpen={isModalOpen}
					setIsModalOpen={setIsModalOpen}
					refreshCallBack={() => refreshData.mutate({})}
					isLoading={refreshData.isLoading}
				/>
				<SendInsightsReportModal
					clientName={clientName}
					isModalOpen={isSendReportModalOpen}
					isBurnDownSuccess={isSuccess}
					setIsModalOpen={setIsSendReportModalOpen}
				/>
			</Grid>

			{isAdvisor && isLoading && <Loader />}
			{isSuccess && <ExportableReport ref={exportableReportRef} />}
		</Grid>
	);
}

SoraWalletHeading.propTypes = {
	client: PropTypes.shape({
		firstName: PropTypes.string,
		lastName: PropTypes.string,
		clientId: PropTypes.number,
		phone: PropTypes.string,
		email: PropTypes.string
	}).isRequired,
	isClient: PropTypes.bool.isRequired,
	advisor: PropTypes.shape({
		name: PropTypes.string
	}).isRequired
};

export default SoraWalletHeading;
